import { ROUTES } from './index';

/** DB string `type:action` for `/users` (see `permissions.type` = `platform-admin.users`). */
export const PLATFORM_ADMIN_USERS_ACCESS_PERMISSION =
  'platform-admin.users:access' as const;

/** DB string `type:action` for `/permissions` (EPIC-UM-23 permission catalog UI). */
export const PLATFORM_ADMIN_PERMISSIONS_ACCESS_PERMISSION =
  'platform-admin.permissions:access' as const;

/**
 * DB string `type:action` for `/roles` (EPIC-31 System-role administration UI).
 *
 * The WRITE key is `platform-admin.roles:manage` and the rank key is
 * `platform-admin.roles.rank:manage`; neither belongs in this map, which gates
 * route ENTRY only. Gating the route on `:manage` would lock read-only platform
 * staff out of a surface they are meant to be able to read.
 */
export const PLATFORM_ADMIN_ROLES_ACCESS_PERMISSION =
  'platform-admin.roles:access' as const;

/** DB string `type:action` for `/errors` (Error Catalog UI). Granted to every Global-scope role. */
export const PLATFORM_ADMIN_ERROR_CATALOG_ACCESS_PERMISSION =
  'platform-admin.error-catalog:access' as const;

/** DB string `type:action` for `/notification-workflows` (EPIC-PA-31 Novu workflow management). */
export const PLATFORM_ADMIN_NOTIFICATION_WORKFLOWS_ACCESS_PERMISSION =
  'platform-admin.notification-workflows:access' as const;

/**
 * DB string `type:action` for MODIFYING a notification workflow: editing
 * template content or tags, rolling back to a stored version, and sending a
 * test notification.
 *
 * Separate from `:access` so the module can be handed out read-only. `:access`
 * still gates reaching the screen — a caller without this one sees the catalog
 * and the history but none of the write affordances, and the backend refuses
 * the four mutating endpoints regardless.
 */
export const PLATFORM_ADMIN_NOTIFICATION_WORKFLOWS_MANAGE_PERMISSION =
  'platform-admin.notification-workflows:manage' as const;

/**
 * DB string `type:action` for `/endpoints` (endpoint coverage, EPIC 25349).
 *
 * The screen used to be exempt from carrying a code because its data came from
 * `development-api-gateway`, which 404s outside dev/staging — the tool was inert
 * in a production build. Feature 3 moved the read path into `admin-api-gateway`,
 * which ships everywhere, so the wiki's own rule applies: a dev tool that lands
 * in a production artifact needs a code like anything else that ships.
 */
export const PLATFORM_ADMIN_ENDPOINT_AUDIT_ACCESS_PERMISSION =
  'platform-admin.endpoint-audit:access' as const;

/**
 * Maps route paths to their required permission.
 * Routes NOT in this map are accessible without permission checks.
 */
export const ROUTE_PERMISSION_MAP: Record<string, string> = {
  [ROUTES.DASHBOARD.OVERVIEW]: 'platform-admin.overview:access',
  [ROUTES.DASHBOARD.USERS]: PLATFORM_ADMIN_USERS_ACCESS_PERMISSION,
  [ROUTES.DASHBOARD.PERMISSIONS]: PLATFORM_ADMIN_PERMISSIONS_ACCESS_PERMISSION,
  [ROUTES.DASHBOARD.ROLES]: PLATFORM_ADMIN_ROLES_ACCESS_PERMISSION,
  [ROUTES.DASHBOARD.I18N.ROOT]: 'platform-admin.i18n.dashboard:access',
  [ROUTES.DASHBOARD.I18N.TRANSLATIONS]:
    'platform-admin.i18n.translations:access',
  [ROUTES.DASHBOARD.I18N.WORKFLOW.PRE_REVIEW]:
    'platform-admin.i18n.workflow.pre-review:access',
  [ROUTES.DASHBOARD.I18N.WORKFLOW.REVIEW]:
    'platform-admin.i18n.workflow.review:access',
  [ROUTES.DASHBOARD.I18N.WORKFLOW.CORRECTIONS]:
    'platform-admin.i18n.workflow.corrections:access',
  [ROUTES.DASHBOARD.I18N.WORKFLOW.CHANGES]:
    'platform-admin.i18n.workflow.changes:access',
  [ROUTES.DASHBOARD.I18N.TOOLS.DUPLICATES]:
    'platform-admin.i18n.tools.duplicates:access',
  // Same permission on both paths while the old route redirects to the new one.
  [ROUTES.DASHBOARD.SETTINGS.REPLICATION]:
    'platform-admin.i18n.tools.replication:access',
  [ROUTES.DASHBOARD.I18N.TOOLS.REPLICATION]:
    'platform-admin.i18n.tools.replication:access',
  [ROUTES.DASHBOARD.TENANTS.ROOT]: 'platform-admin.tenants:access',
  [ROUTES.DASHBOARD.ANALYTICS]: 'platform-admin.analytics:access',
  [ROUTES.DASHBOARD.BUGS.ROOT]: 'platform-admin.bugs:access',
  [ROUTES.DASHBOARD.BUGS.DASHBOARD]: 'platform-admin.bugs:access',
  [ROUTES.DASHBOARD.ERROR_CATALOG]: PLATFORM_ADMIN_ERROR_CATALOG_ACCESS_PERMISSION,
  [ROUTES.DASHBOARD.ENDPOINTS.ROOT]:
    PLATFORM_ADMIN_ENDPOINT_AUDIT_ACCESS_PERMISSION,
  [ROUTES.DASHBOARD.NOTIFICATION_WORKFLOWS.ROOT]:
    PLATFORM_ADMIN_NOTIFICATION_WORKFLOWS_ACCESS_PERMISSION,
  [ROUTES.DASHBOARD.SETTINGS.I18N]: 'platform-admin.settings.i18n:access',
};

/**
 * Routes this Surface serves WITHOUT a permission code, each with the reason it
 * is exempt.
 *
 * ## Why a registry and not an absence
 *
 * Requirements declared this Surface's default on 2026-08-25: **a screen with
 * no mapped code is DENIED, not admitted.** That follows the Explicit Grant
 * Model — a permission that is not listed is denied — and the route guard was
 * the one place doing the opposite.
 *
 * Under a closed default, silence means "nobody reaches it", which is the exact
 * opposite of what an exemption intends. So an exemption has to be WRITTEN to
 * exist, and this map is where it is written. The value is the reason, because
 * the reasons are not interchangeable and requirements was explicit about that:
 *
 * - A **distribution** decision — every authenticated member of this Surface may
 *   see it, and no finer distinction is wanted. Revisable: someone can later
 *   decide the screen does need a code.
 * - A **self-scoped** screen — its content is bounded by WHO IS ASKING, not by
 *   what they hold. A screen showing a person their own records has nothing a
 *   permission could widen or narrow. That property survives any distribution
 *   decision.
 *
 * The point of the closed default is not these screens; it is the NEXT screen
 * somebody adds without a code. With the default closed that is a defect
 * somebody finds. With it open it is a permission nobody granted and nobody
 * notices.
 */
export const ROUTE_EXEMPTIONS: Record<string, string> = {
  // ── Ratified by requirements, 2026-08-25 ────────────────────────────────
  /** Distribution decision: the development backlog and the team PTO calendar. */
  '/azure-backlog': 'staff-wide by decision',
  /** Distribution decision: browses the snapshots the Figma Snapshot Tool pushes. */
  '/figma/snapshots': 'staff-wide by decision',
  /** Self-scoped: a person's own tickets. Nothing a permission could widen. */
  '/my-tickets': 'self-scoped',

  /**
   * Self-scoped, exactly as My Tickets: the caller's own profile and API keys.
   *
   * Ratified 2026-08-25, one delivery after we exempted it to preserve
   * behaviour. Requirements admitted it under the SELF-SCOPED reason rather
   * than the distribution one, and said why the distinction earned its keep:
   * "a category with one member reads as a special case; with two it reads as
   * a category."
   */
  '/settings/profile': 'self-scoped',

  // `/endpoints` used to be exempted here as a development-only tool the
  // closed default would otherwise lock out. It is GATED now, on
  // `platform-admin.endpoint-audit:access` (EPIC 25349, arrived on develop
  // 2026-08-26), so the exemption came out: a route cannot be both mapped and
  // exempt, and the co-located test refuses that combination rather than
  // letting the map quietly win. Requirements' own rule reaches the same place
  // — a tool that ships "needs a code like anything else that ships".

  // ── Not a distribution decision at all ──────────────────────────────────
  /**
   * The guard's own redirect target. Denying the page a denial sends you to is
   * a loop, so this is a safety property rather than a decision about who may
   * see what. It lives outside `(dashboard)` today and the hook is only mounted
   * inside it, so nothing reaches this entry now — it is here so that stays
   * true if the guard is ever mounted higher.
   *
   * `/dev-tools/observability-check` is outside `(dashboard)` for the same
   * reason and deliberately has NO entry: the guard never runs on it, and an
   * exemption for a route the guard cannot see reads as a decision nobody made.
   * Move it under `(dashboard)` and it needs one.
   */
  '/access-denied': 'the denial page itself — never deny the redirect target',
};

/**
 * The ONE route gated by the gateway's answer instead of by a code in the
 * active role's permissions: the MCP console.
 *
 * Decision 2026-09-29: everything MCP follows the UNION of the permissions of
 * every role the user holds, never the active role. The browser only ever sees
 * the active role's permissions, so it cannot compute that union; the guard and
 * the sidebar ask `GET /platform/mcp-connections/v1.0/access` instead (hook:
 * `useMcpConnectionsAccess`). It is therefore neither in `ROUTE_PERMISSION_MAP`
 * (a mapped code would be checked against the active role) nor in
 * `ROUTE_EXEMPTIONS` (it is not open to everyone).
 */
export const UNION_GATED_ROUTE = ROUTES.DASHBOARD.MCP_CONNECTIONS;

/** Exact match first, then the longest prefix — shared by both lookups. */
function longestPrefixMatch(
  pathname: string,
  keys: string[]
): string | undefined {
  if (keys.includes(pathname)) return pathname;
  return keys
    .filter((route) => pathname.startsWith(route + '/'))
    .sort((a, b) => b.length - a.length)[0];
}

/**
 * Returns the required permission for a given pathname.
 * Uses exact match first, then longest prefix match for dynamic sub-routes.
 */
export function getRequiredPermission(pathname: string): string | undefined {
  const match = longestPrefixMatch(pathname, Object.keys(ROUTE_PERMISSION_MAP));
  return match ? ROUTE_PERMISSION_MAP[match] : undefined;
}

/**
 * Whether the route is one of the written exemptions above.
 *
 * Distinct from "has no permission mapped": that is now a DENIAL, and telling
 * the two apart is the whole point of the closed default.
 */
export function isRouteExempt(pathname: string): boolean {
  return longestPrefixMatch(pathname, Object.keys(ROUTE_EXEMPTIONS)) !== undefined;
}

/** Whether the route is (under) `UNION_GATED_ROUTE`. */
export function isRouteUnionGated(pathname: string): boolean {
  return longestPrefixMatch(pathname, [UNION_GATED_ROUTE]) !== undefined;
}

/**
 * Returns the first route (in insertion order) the user has permission to access.
 * Used as fallback redirect when the target route is not permitted.
 */
export function getFirstPermittedRoute(
  userPermissions: string[],
): string | undefined {
  const permissionSet = new Set(userPermissions);

  for (const [route, permission] of Object.entries(ROUTE_PERMISSION_MAP)) {
    if (permissionSet.has(permission)) return route;
  }

  return undefined;
}

/**
 * The route a user is sent to when they land on `/`, hit a denied route, or
 * switch role on Access Denied (AB#28616): the first mapped route the active
 * role holds, else the MCP console when the gateway allows it, else none.
 *
 * MCP ranks LAST on purpose: it is the one route answered by the union of the
 * user's roles rather than the active one, so any screen the active role does
 * hold comes first. `mcpAllowed` is the `useMcpConnectionsAccess` answer; the
 * caller must not pass a value it has not received yet (`useFirstPermittedRoute`
 * waits for it, and only when no mapped route exists).
 */
export function resolveFallbackRoute(
  userPermissions: string[],
  mcpAllowed: boolean,
): string | undefined {
  return (
    getFirstPermittedRoute(userPermissions) ??
    (mcpAllowed ? UNION_GATED_ROUTE : undefined)
  );
}
